/* eslint-disable react/no-unescaped-entities */
/**
 * Tutorial content — single source of truth for the /tutorials hub.
 * Each entry: { id, title, category, icon, summary, sections[] }
 * Sections use a small shape language so the page can render consistently:
 *   { type: 'paragraph' | 'steps' | 'tip' | 'warning' | 'list' | 'heading', text, items }
 */
import {
  LayoutDashboard, Wand2, Sparkles, BookOpen, Megaphone, Mail, Plug,
  CalendarDays, FileText, Archive, Briefcase, Building2, Shield,
  CreditCard, Users, Image as ImageIcon, Download, ShieldCheck,
  CalendarRange, Send, Globe, Bookmark, FileSpreadsheet, ToggleLeft,
  Palette, Rocket, BarChart3, UserPlus,
} from 'lucide-react';

export const TUTORIAL_CATEGORIES = [
  { id: 'getting-started', label: 'Getting started' },
  { id: 'content', label: 'Create content' },
  { id: 'publish', label: 'Publish & schedule' },
  { id: 'workflow', label: 'Workflow' },
  { id: 'agency', label: 'Agency workflow' },
  { id: 'platform-setup', label: 'Platform setup (App Review)' },
  { id: 'account', label: 'Account & billing' },
  { id: 'admin', label: 'Admin (platform owner)' },
];

export const TUTORIALS = [
  // ─── Getting started ────────────────────────────────────────────────
  {
    id: 'dashboard',
    title: 'The Dashboard',
    category: 'getting-started',
    icon: LayoutDashboard,
    summary: 'Your home screen — at-a-glance stats and the fastest path to every feature.',
    tryLink: { href: '/dashboard', label: 'Open Dashboard' },
    sections: [
      { type: 'paragraph', text: 'The Dashboard shows how many posts you have, what\'s scheduled next, and quick links to every major feature. Use it as your "what do I work on today?" screen.' },
      { type: 'heading', text: 'What you\'ll see' },
      { type: 'list', items: [
        'Top stats: total posts, scheduled, published, failed',
        'Next upcoming scheduled post with countdown',
        'Quick action cards: Generate, AI Post Manager, Calendar',
        'Usage meter in the sidebar — AI images + posts remaining this month',
      ]},
      { type: 'tip', text: 'The usage meter turns amber at 80% and red when exhausted. Click it any time to see your plan and upgrade options.' },
    ],
  },
  {
    id: 'connect-accounts',
    title: 'Connect social accounts',
    category: 'getting-started',
    icon: Plug,
    summary: 'Link LinkedIn, Facebook, Instagram, X, and YouTube so the app can actually publish for you.',
    tryLink: { href: '/connections', label: 'Go to Connections' },
    sections: [
      { type: 'paragraph', text: 'Before any post can auto-publish, you need to connect the accounts/pages you want to post to. Each connection uses the official OAuth flow — we never ask for passwords.' },
      { type: 'heading', text: 'Steps' },
      { type: 'steps', items: [
        'Open /connections from the sidebar.',
        'Click "Connect" next to the platform you want. A new tab opens the official OAuth page.',
        'Authorize the requested permissions. You\'ll be redirected back automatically.',
        'For Facebook & Instagram: select every page / IG account you want to manage (you can add more later).',
        'You should see a green "Connected" badge. That account is now available as a posting destination.',
      ]},
      { type: 'heading', text: 'What each platform can do' },
      { type: 'list', items: [
        'LinkedIn — personal profile + company pages',
        'Facebook — your own profile + any Pages you admin',
        'Instagram — any Business/Creator account linked to a connected Facebook page',
        'Bluesky — one account per connection',
        'YouTube — one channel per connection (for Shorts + long-form)',
        'TikTok — coming soon (awaiting platform approval)',
      ]},
      { type: 'tip', text: 'You can tag connected destinations under a "Client" on the Clients page, then filter your whole workspace by that client.' },
    ],
  },

  // ─── Create content ─────────────────────────────────────────────────
  {
    id: 'generate-post',
    title: 'Generate a single post',
    category: 'content',
    icon: Wand2,
    summary: 'The fastest way to draft one post — caption, hashtags, and image prompt — in under 30 seconds.',
    tryLink: { href: '/generate', label: 'Try the Generator' },
    sections: [
      { type: 'paragraph', text: 'The Generator page is for ad-hoc single-post creation. Give it a topic and platform — the AI returns a caption, hashtag set, and an image prompt. Best for moments when you want one post, not a full campaign.' },
      { type: 'heading', text: 'Steps' },
      { type: 'steps', items: [
        'Fill in Topic (what the post is about) and Audience (who you want to reach).',
        'Pick a Tone — Professional, Casual, Witty, Inspirational, etc.',
        'Pick one or more Platforms. The AI auto-tunes tone + length per platform.',
        'Choose Post Type: Standard, Carousel (multi-slide), or Short-form Video script.',
        'Click Generate. The draft appears in seconds and is auto-saved to My Posts.',
      ]},
      { type: 'tip', text: 'Generated posts are saved as drafts — they don\'t publish automatically. Open the post card to edit, generate an image, and schedule it.' },
    ],
  },
  {
    id: 'ai-post-manager',
    title: 'AI Post Manager (campaigns)',
    category: 'content',
    icon: Sparkles,
    summary: 'Draft a 30-day multi-platform campaign in ~17 seconds. The most powerful feature in the app.',
    tryLink: { href: '/ai-manager', label: 'Open AI Post Manager' },
    sections: [
      { type: 'paragraph', text: 'AI Post Manager takes one campaign brief (e.g. "mental health awareness for BIPOC communities") and drafts every single post across every platform for the whole window. Each post gets a tuned caption, hashtags, image prompt, and a smart posting time.' },
      { type: 'heading', text: 'Step-by-step' },
      { type: 'steps', items: [
        'Open /ai-manager from the sidebar.',
        'Fill the brief: prompt, audience, tone. Pick the platforms you want posts for.',
        'Set the date range and posts-per-day-per-platform cap (max 5/day).',
        '(Optional) Tag the campaign for a specific Client.',
        'Click "Generate plan". The preview panel fills with every draft post.',
        'Switch to Quick Scan view for big campaigns — drafts group into weekly accordions so you can approve week-by-week.',
        'Click "Save as drafts" to persist them, then "Approve All & Schedule" to flip everything to scheduled.',
      ]},
      { type: 'heading', text: 'Auto-generate images on approval' },
      { type: 'paragraph', text: 'On the Approve & Schedule dialog, check "Auto-generate images for all posts". The app runs image generation in the background using gpt-image-1 (supported on every account) so every post has a visual by publish time. Each image counts toward your monthly AI image quota.' },
      { type: 'tip', text: 'A 30-day × 2-platform × 2/day campaign generates 120 posts. Without the auto-image toggle, you\'d need to manually click "Generate image" on every post — don\'t forget to toggle it on.' },
      { type: 'warning', text: 'If your image quota runs out mid-batch, remaining posts publish text-only rather than failing. Upgrade your plan to avoid hitting the cap.' },
    ],
  },
  {
    id: 'ai-blog',
    title: 'AI Blog',
    category: 'content',
    icon: BookOpen,
    summary: 'Long-form SEO article with title, meta description, sections, and a featured image prompt.',
    tryLink: { href: '/studio/blog', label: 'Try AI Blog' },
    sections: [
      { type: 'paragraph', text: 'Writes a ready-to-paste blog article, not a social caption. Output is structured: title, slug, meta description, intro, sectioned body, conclusion, CTA, and a featured image prompt.' },
      { type: 'heading', text: 'Steps' },
      { type: 'steps', items: [
        'Open /studio/blog.',
        'Fill in Topic, Audience, Tone. Add optional SEO keywords to weave in naturally.',
        'Set your target Word Count (300–3000).',
        'Click Generate. The structured article appears below.',
        'Copy sections into WordPress, Ghost, Substack, etc. Use the featured_image_prompt with "Generate image" for a matching hero image.',
      ]},
      { type: 'tip', text: 'Blog posts appear in /posts with post_type "blog" — filter by platform "Blog" to see them all.' },
    ],
  },
  {
    id: 'ai-email',
    title: 'AI Email',
    category: 'content',
    icon: Mail,
    summary: '3 subject lines, preheader, and both plaintext + HTML body — ready for Mailchimp / Klaviyo / SendGrid.',
    tryLink: { href: '/studio/email', label: 'Try AI Email' },
    sections: [
      { type: 'paragraph', text: 'Generates a complete marketing email. You get 3 subject-line options for A/B testing, a preheader (the preview text that shows next to the subject in inboxes), plaintext body, AND HTML body, plus CTA copy and a sign-off.' },
      { type: 'heading', text: 'Steps' },
      { type: 'steps', items: [
        'Open /studio/email.',
        'Fill Topic, Audience, Tone.',
        'Pick Email Type — newsletter, promotional, welcome, nurture, abandoned cart, announcement.',
        'Add optional CTA (e.g. "Start free trial").',
        'Click Generate. Copy either the plaintext or HTML body into your ESP.',
      ]},
      { type: 'tip', text: 'Always test the primary subject against the 2 alternates — the AI typically writes one safe option and two more creative ones.' },
    ],
  },
  {
    id: 'ai-ads',
    title: 'AI Ads',
    category: 'content',
    icon: Megaphone,
    summary: 'Multiple ad variants sized exactly to Google, Meta, or LinkedIn ad character limits.',
    tryLink: { href: '/studio/ad', label: 'Try AI Ads' },
    sections: [
      { type: 'paragraph', text: 'Generates ready-to-paste ad variants with character limits that match the real platform spec. Pick 1–5 variants per run.' },
      { type: 'heading', text: 'Supported platforms' },
      { type: 'list', items: [
        'Google Ads — up to 15 headlines (≤30ch), 4 descriptions (≤90ch), path1/path2',
        'Meta Ads — primary_text (≤125ch), 5 headlines (≤40ch), 5 descriptions (≤30ch)',
        'LinkedIn Ads — intro_text (≤150ch), 5 headlines (≤70ch), 5 descriptions (≤70ch)',
      ]},
      { type: 'heading', text: 'Steps' },
      { type: 'steps', items: [
        'Open /studio/ad.',
        'Describe your Product/Offer. Set Audience, Tone, and platform.',
        'Pick how many variants you want (more = more for A/B testing).',
        'Click Generate. Each variant comes with matching image prompts.',
        'Paste directly into Google Ads Editor, Meta Ads Manager, or LinkedIn Campaign Manager.',
      ]},
    ],
  },
  {
    id: 'image-generation',
    title: 'AI image generation',
    category: 'content',
    icon: ImageIcon,
    summary: 'Pick the right model for your use case — cost vs. quality.',
    sections: [
      { type: 'paragraph', text: 'Every post card has a "Generate image" button. You can also batch-generate images via AI Post Manager\'s Approve All toggle.' },
      { type: 'heading', text: 'Which model to use' },
      { type: 'list', items: [
        'GPT Image 1 — Default. Great for marketing graphics. Flat ~$0.02/image. Works on every account.',
        'DALL·E 3 — OpenAI alternative. Strong creative output. Available with a personal OpenAI key.',
        'Nano Banana (Gemini) — Best for photoreal lifestyle shots. ~$0.04/image.',
      ]},
      { type: 'heading', text: 'Quota behavior' },
      { type: 'list', items: [
        'Every generation counts toward ai_images_per_month on your plan (5/300/800/1500 for Trial/Starter/Growth/Pro).',
        'Image UPLOADS (your own photos) don\'t touch the quota — upload freely.',
        'The sidebar usage meter shows your remaining balance in real time.',
      ]},
      { type: 'tip', text: 'Platform owner can paste a personal OpenAI API key in Admin → Settings → OpenAI to unlock additional models (e.g. dall-e-3 / gpt-image-2) the Universal Key doesn\'t carry.' },
    ],
  },
  {
    id: 'creative-studio',
    title: 'AI Creative Studio & Brand Kits',
    category: 'content',
    icon: Palette,
    summary: 'Turn AI copy into branded, ready-to-post graphics — single visuals or a whole branded campaign.',
    tryLink: { href: '/studio/creative', label: 'Open Creative Studio' },
    sections: [
      { type: 'paragraph', text: 'The AI Creative Studio designs finished social graphics, not just captions. The AI writes the on-image copy, your Brand Kit (logo, colors, fonts) is applied automatically, and the image is rendered in your browser and saved straight to My Posts — no extra image-generation cost.' },
      { type: 'heading', text: 'Step 1 — Save a Brand Kit (once)' },
      { type: 'steps', items: [
        'Open /brand-kit.',
        'Upload your logo, set your primary + accent colors, and pick a font.',
        'Save. Every creative you make from now on uses this kit automatically.',
      ]},
      { type: 'heading', text: 'Step 2 — Single creative' },
      { type: 'steps', items: [
        'Open /studio/creative (Single creative mode).',
        'Enter a topic and click Generate copy — the AI writes the headline + subheadline.',
        'Pick a template (Spotlight / Card / Split) and a size (1:1, 4:5, 9:16, 16:9).',
        'Use Edit layout to drag the text, resize the headline, recolor, and adjust the photo overlay.',
        'Click Save as draft (lands in My Posts) or Download the PNG.',
      ]},
      { type: 'heading', text: 'Step 3 — Branded campaign mode' },
      { type: 'paragraph', text: 'Switch the toggle at the top to "Branded campaign" to auto-create a whole scheduled campaign where every AI image is brand-framed with that post\'s own headline, your logo, and brand colors. Captions + hashtags are written for you.' },
      { type: 'steps', items: [
        'Enter one campaign brief, pick platforms, date range, posts/day, and tone.',
        'Under "Publish to (account)", choose exactly which connected account(s) each platform should post to.',
        'Pick the Brand Kit + size, then click Generate campaign preview.',
        'Review the planned posts, then click Schedule — branded images generate in the background.',
      ]},
      { type: 'tip', text: 'Brand-framing the AI photo is the closest thing to Predis.ai-style output — fully designed graphics across an entire campaign in one pass.' },
    ],
  },

  // ─── Publish & Schedule ─────────────────────────────────────────────
  {
    id: 'schedule',
    title: 'Schedule & auto-publish',
    category: 'publish',
    icon: CalendarDays,
    summary: 'Queue posts for the exact time you want — the app publishes for you.',
    tryLink: { href: '/calendar', label: 'Open Calendar' },
    sections: [
      { type: 'paragraph', text: 'Once a post is a draft, open it and click Schedule. Pick a date/time + target accounts. At the scheduled time, the background worker calls the platform\'s official API and publishes.' },
      { type: 'heading', text: 'Multi-page posting' },
      { type: 'paragraph', text: 'When scheduling, check multiple destinations (e.g. 3 Facebook pages + 2 Instagram accounts). Each selected destination becomes its own scheduled post with an AI-tuned caption variant. You manage them independently after creation.' },
      { type: 'heading', text: 'Calendar view' },
      { type: 'list', items: [
        '/calendar shows a month grid with every scheduled post',
        'Filter by platform, status, or Client',
        'Small color dots on day-chips show which Client each post belongs to',
        'Click a day to see all posts scheduled for it',
      ]},
      { type: 'warning', text: 'The scheduler needs the relevant social account connected at publish time. If you disconnect Facebook, queued FB posts will fail.' },
    ],
  },
  {
    id: 'posts-page',
    title: 'My Posts — sort, filter, bulk actions',
    category: 'publish',
    icon: FileText,
    summary: 'One page to manage every post — with multi-select bulk archive/delete.',
    tryLink: { href: '/posts', label: 'Open My Posts' },
    sections: [
      { type: 'paragraph', text: '/posts shows every non-archived post. Use the filter row at the top to narrow down, and the Sort dropdown to reorder.' },
      { type: 'heading', text: 'Filters & sort' },
      { type: 'list', items: [
        'Platform filter — pick one platform',
        'Type filter — posts / carousels / videos / blogs / ads / emails',
        'Saved filter — show only posts you starred',
        'Client filter (if you use the Agency workflow)',
        'Sort by: Newest, Oldest, Scheduled soonest / latest, By status, By platform',
      ]},
      { type: 'heading', text: 'Bulk actions' },
      { type: 'steps', items: [
        'Click "Select" to enter selection mode — checkboxes appear on every card.',
        'Tap cards to select them (or click the checkbox). Use "Select all" in the toolbar to grab everything visible.',
        'Click "Archive (N)" or "Delete (N)". Delete asks for confirmation.',
        'Click "Done" to exit selection mode.',
      ]},
      { type: 'tip', text: 'Every published post card now has a "View live ↗" link that opens the real post on LinkedIn / Facebook / Instagram / etc.' },
    ],
  },
  {
    id: 'launch-full-campaign',
    title: 'Launch full campaign (1-click)',
    category: 'publish',
    icon: Rocket,
    summary: 'Turn a previewed plan into a fully-wired funnel — scheduled posts, branded images, and a matching email sequence — in one click.',
    tryLink: { href: '/ai-manager', label: 'Open AI Post Manager' },
    sections: [
      { type: 'paragraph', text: 'After AI Post Manager generates a campaign preview, the "Launch full campaign" button does everything at once: schedules every post, queues image generation, and (optionally) drafts a matching newsletter + nurture sequence.' },
      { type: 'heading', text: 'In the Launch dialog' },
      { type: 'steps', items: [
        'Pick which connected account(s) to publish to for each platform (checkboxes, one per page/account).',
        'Toggle "Auto-generate images" — and "Brand-frame images" to overlay your Brand Kit logo + colors on each photo.',
        'Optionally keep "Draft emails" on to auto-create a newsletter + sequence for the campaign.',
        'Click Launch — posts schedule, images render in the background, emails appear as drafts.',
      ]},
      { type: 'tip', text: 'Brand-framing here uses your saved Brand Kit + the post\'s own headline, so a whole campaign comes out on-brand without manual design.' },
    ],
  },
  {
    id: 'sent-engagement',
    title: 'Sent tab & live engagement',
    category: 'publish',
    icon: BarChart3,
    summary: 'See your published posts with a live link and real likes/comments pulled from the platform.',
    tryLink: { href: '/posts?tab=sent', label: 'Open Sent' },
    sections: [
      { type: 'paragraph', text: 'The "Sent" tab in My Posts lists everything that has actually published, with the date/time it went out and a direct "View on platform" link.' },
      { type: 'heading', text: 'Live engagement stats' },
      { type: 'list', items: [
        'For Facebook, Instagram and LinkedIn, the card pulls live likes + comments using your existing connection.',
        'If a platform doesn\'t support stats (or a token expired), the card shows "Unavailable" instead of breaking.',
        'Stats refresh when you open the tab — no separate analytics setup needed.',
      ]},
      { type: 'tip', text: 'Use the Sent tab as a quick pulse-check on which topics land best, then feed the winners back into your next campaign brief.' },
    ],
  },

  // ─── Workflow / Agency additions (Feb 2026) ─────────────────────────
  {
    id: 'weekly-themes',
    title: 'Weekly Themes — auto-schedule weeks of content',
    category: 'workflow',
    icon: CalendarRange,
    summary: 'Define a 7-day topic rotation once. Generate up to 90 days of pre-scheduled posts in one click.',
    tryLink: { href: '/weekly-themes', label: 'Open Weekly Themes' },
    sections: [
      { type: 'paragraph', text: 'Weekly Themes is the fastest way to fill your content calendar. Set what topic to post on each day of the week (Mon = product updates, Tue = customer story, etc.) and the system generates and schedules every post for you — for 1 week, 30 days, or up to 90 days.' },
      { type: 'heading', text: 'How it works' },
      { type: 'steps', items: [
        'Click "New theme" or apply one of the built-in starter templates.',
        'Set default tone, audience, and which platforms posts should fan out to.',
        'Set each weekday\'s topic. Optionally override tone, time, or style profile per day.',
        'Pick a Run length (1 week / 30 days / 60 / 90, or custom 1–13 weeks).',
        'Click "Generate" → choose Drafts (review first) or Scheduled (auto-publish).',
      ]},
      { type: 'heading', text: 'Pro features' },
      { type: 'list', items: [
        'Per-day Style Profile override — Monday posts in casual voice, Friday in formal',
        '"Preview Voice" button before generating — hear how your tone sounds',
        '"Save preview as draft" — pull a sample post into your drafts to refine first',
        'Starter Templates gallery — pre-built rotations for real-estate, restaurant, SaaS, etc.',
        'Save your own theme as a reusable template (Personal or Global if admin)',
      ]},
      { type: 'tip', text: 'The weekly pattern REPEATS. If you pick 4 weeks (~30 days), Monday\'s topic runs all 4 Mondays in a row. Edit individual posts after generation if you need uniqueness.' },
      { type: 'warning', text: 'Locked on Free + Starter plans by default. Admin can flip access per-plan in Admin → Features.' },
    ],
  },
  {
    id: 'url-generator',
    title: 'Scan URL → Auto Post',
    category: 'content',
    icon: Globe,
    summary: 'Paste any webpage URL and the system writes a captioned post + image prompt from the scraped content.',
    tryLink: { href: '/url-generator', label: 'Open URL Generator' },
    sections: [
      { type: 'paragraph', text: 'Got a blog article, product launch page, or news story you want to share? Drop the URL and the app scrapes the headline + body, runs it through the AI, and gives you a ready-to-publish post.' },
      { type: 'heading', text: 'Steps' },
      { type: 'steps', items: [
        'Open /url-generator from the sidebar (or from Generate → Scan URL).',
        'Paste the URL. Click "Scan".',
        'The page shows the extracted title and body so you can confirm we read it right.',
        'Pick tone, platforms, and (optional) a Style Profile.',
        'Click Generate. Edit the result inline before saving as a draft or scheduling.',
      ]},
      { type: 'tip', text: 'Works on most public webpages. Paywalled articles, JS-heavy SPAs, and login-gated pages may scrape with limited content. If a scrape comes back short, paste the body text directly into the prompt instead.' },
    ],
  },
  {
    id: 'sent-reviews',
    title: 'Sent Reviews — track client magic-link approvals',
    category: 'agency',
    icon: Send,
    summary: 'See which posts you sent to clients for approval, who responded, and what they said.',
    tryLink: { href: '/posts?tab=reviews', label: 'Open Sent Reviews' },
    sections: [
      { type: 'paragraph', text: 'When you send a draft for client review (the "Send for client review" button on any post), the system emails the client a magic link. They can approve, request changes, or reject — without needing a PostToSocial account. The "Sent Reviews" tab inside My Posts is your tracking dashboard.' },
      { type: 'heading', text: 'The lifecycle of a review' },
      { type: 'steps', items: [
        'You click "Send for client review" on a draft → enter the client\'s email → magic link emailed.',
        'Status shows as 🟡 Pending in the Sent Reviews tab.',
        'Client opens the link, sees the post, and clicks Approve / Request changes / Reject.',
        'Status flips to 🟢 Approved / 🟠 Changes / 🔴 Rejected with their comment underneath.',
        'For approved posts, schedule + publish as normal. For rejected ones, revise the draft and resend.',
      ]},
      { type: 'heading', text: 'Actions per row' },
      { type: 'list', items: [
        'Copy link — copy the magic URL to send via Slack / SMS instead of email',
        'Cancel — kill the link before the client opens it (e.g. wrong email)',
        'View — open the client-facing review page yourself to check what they see',
      ]},
      { type: 'tip', text: 'Filter chips at the top let you focus on only Pending or Rejected reviews — useful when chasing approvals at end-of-week.' },
    ],
  },
  {
    id: 'client-portal',
    title: 'Client Portal — invite clients to connect & review',
    category: 'agency',
    icon: UserPlus,
    summary: 'Email a client a link so they connect their own accounts and approve posts — without ever being able to post.',
    tryLink: { href: '/organizations', label: 'Open Organizations' },
    sections: [
      { type: 'paragraph', text: 'The Client Portal lets a brand\'s own contact connect their social accounts and review what\'s going out, in a locked-down read-only space. The client provides the authorization; you (the agency) keep doing the posting.' },
      { type: 'heading', text: 'Step 1 — Invite the client' },
      { type: 'steps', items: [
        'Open an Organization → click Invite.',
        'Set the role to "Client (read-only portal)".',
        'Pick which Client/brand they represent, enter their email, and send.',
        'They receive a secure invite link (valid 7 days).',
      ]},
      { type: 'heading', text: 'Step 2 — Client connects their accounts' },
      { type: 'steps', items: [
        'The client clicks the link, sets a password, and lands on their Portal.',
        'They click Connect Facebook / Instagram / LinkedIn and authorize via the official login.',
        'Those accounts are added to YOUR account pool, automatically tagged to their brand — so they appear as publishing destinations for you.',
      ]},
      { type: 'heading', text: 'Step 3 — Client reviews (never posts)' },
      { type: 'list', items: [
        'The client sees their brand\'s scheduled + published posts with status and a live link.',
        'They can Approve or Request changes (with a note) — you get notified.',
        'They have no compose or publish button; the backend blocks any posting from a portal account.',
        'Either side can disconnect an account at any time.',
      ]},
      { type: 'tip', text: 'This is the lowest-friction way to onboard a client: you never handle their passwords, and they stay in control of their own access grant.' },
    ],
  },
  {
    id: 'bulk-client-import',
    title: 'Bulk Client Import (CSV)',
    category: 'agency',
    icon: FileSpreadsheet,
    summary: 'Onboard 30+ clients in one shot from a spreadsheet — no manual entry.',
    tryLink: { href: '/clients', label: 'Open Clients' },
    sections: [
      { type: 'paragraph', text: 'If you\'re migrating from another tool or onboarding a batch of clients at once, the CSV importer handles it in seconds. Up to 100 clients per upload.' },
      { type: 'heading', text: 'CSV format' },
      { type: 'list', items: [
        'Required header row: name, email, brand_color (color is optional but recommended)',
        'One row per client',
        'Email is used for magic-link reviews — accurate emails matter',
        'Brand color in #RRGGBB format (e.g. #4F46E5) — appears as the client badge color',
      ]},
      { type: 'heading', text: 'Steps' },
      { type: 'steps', items: [
        'Click "Import CSV" on /clients.',
        'Drop your file. The dialog shows row-by-row validation — fix errors before importing.',
        'Click Import. Clients appear in the list ready to assign connections to.',
        'Optionally assign FB Pages / IG accounts / LinkedIn pages to each client from /clients.',
      ]},
      { type: 'warning', text: 'Duplicate emails are skipped — the importer doesn\'t overwrite existing client records. Delete first if you want to re-import.' },
    ],
  },
  {
    id: 'feature-flags',
    title: 'Feature Flags — admin controls (per-plan access)',
    category: 'admin',
    icon: ToggleLeft,
    summary: 'Toggle which subscription tiers can use each feature. Roll out features to Starter as promotions, lock pro features behind paid tiers.',
    tryLink: { href: '/admin?tab=features', label: 'Open Admin → Features' },
    sections: [
      { type: 'paragraph', text: 'Feature Flags let the platform owner control which subscription plans get access to each feature, without code changes. Need to promote Weekly Themes to Starter users for a week? One click. Want to gate AI Post Manager behind Pro? Same.' },
      { type: 'heading', text: 'How to use' },
      { type: 'steps', items: [
        'Sign in as admin → Admin Dashboard → Features tab.',
        'Find the feature row (organized by Agency / Content / Analytics categories).',
        'Tick / untick the box for each tier you want to allow.',
        'Changes apply instantly to every user — they\'ll see an "Upgrade" card if they hit a locked feature.',
      ]},
      { type: 'heading', text: 'Catalogued features (out of the box)' },
      { type: 'list', items: [
        'Weekly Themes, Client Management, Magic-link Reviews, Bulk CSV Import',
        'Brand Voice / Style Profiles, Scan URL → Auto Post, Auto-publish, AI Post Manager',
        'Campaign Manager, Advanced Analytics, CSV Export',
      ]},
      { type: 'tip', text: 'You (admin) bypass every flag — so testing locked features as yourself won\'t reproduce a user\'s lock screen. Use the impersonate feature in the Users tab to see the experience as a Free or Starter user.' },
      { type: 'warning', text: 'Admin-only tutorial — only the platform owner sees the Features tab.' },
    ],
  },
  {
    id: 'changelog',
    title: 'Changelog — see what\'s new',
    category: 'getting-started',
    icon: Sparkles,
    summary: 'A running log of every feature, fix, and improvement shipped to the platform.',
    tryLink: { href: '/changelog', label: 'Open Changelog' },
    sections: [
      { type: 'paragraph', text: 'Want to know what\'s changed since your last visit? The Changelog page lists every notable feature, improvement, and bug fix grouped by date. Pinned at the top: the latest 5 releases.' },
      { type: 'heading', text: 'What you\'ll find' },
      { type: 'list', items: [
        'New features (highlighted in green)',
        'Improvements to existing flows (blue)',
        'Bug fixes (amber)',
        'Each entry links to the relevant tutorial or page',
      ]},
      { type: 'tip', text: 'Subscribe to the changelog RSS via the icon in the top-right corner of /changelog if you want updates in your reader.' },
    ],
  },
  {
    id: 'archive',
    title: 'Archive',
    category: 'publish',
    icon: Archive,
    summary: 'Published posts auto-move here so your active feed stays clean.',
    tryLink: { href: '/archive', label: 'Open Archive' },
    sections: [
      { type: 'paragraph', text: 'After a post successfully publishes, it automatically moves to /archive. Failed or scheduled posts stay in /posts where you can retry or reschedule them.' },
      { type: 'heading', text: 'What you can do in Archive' },
      { type: 'list', items: [
        'Filter by platform + client',
        'Click any card to view the full original post and its published URL',
        'Restore a post back to active state (via the post detail view)',
      ]},
    ],
  },

  // ─── Workflow ───────────────────────────────────────────────────────
  {
    id: 'clients',
    title: 'Clients (Agency workflow)',
    category: 'workflow',
    icon: Briefcase,
    summary: 'Group 30+ connected destinations into named Clients and filter your entire workspace.',
    tryLink: { href: '/clients', label: 'Open Clients' },
    sections: [
      { type: 'paragraph', text: 'If you run multiple brands, Clients let you tag connected accounts (FB pages, IG accounts, LinkedIn pages) under a named client with its own brand color. Then every post created for that client is auto-tagged.' },
      { type: 'heading', text: 'Setup' },
      { type: 'steps', items: [
        'Open /clients.',
        'Click "New client". Give it a name, pick a brand color (shows as the badge color everywhere), add notes.',
        'Open "Manage destinations" on the new client card.',
        'Check every FB page / IG account / X handle that belongs to this client. Save.',
        'Those destinations now show a color badge across Posts, Calendar, Archive, AI Post Manager.',
      ]},
      { type: 'heading', text: 'Using the filter' },
      { type: 'list', items: [
        'Every feed (Posts, Calendar, Archive, AI Post Manager) has a Client filter pill in the header',
        'Pick a client to see ONLY posts for that brand',
        'When creating a new post, tagging it with a client (or picking a tagged destination) auto-inherits the client_id',
      ]},
      { type: 'tip', text: 'Set a brand color that matches each client\'s real brand — the color dot on day-chips in the calendar becomes instantly recognizable.' },
    ],
  },
  {
    id: 'organizations',
    title: 'Organizations (teams)',
    category: 'workflow',
    icon: Building2,
    summary: 'Shared workspaces for teams — members, roles, and org-scoped posts.',
    tryLink: { href: '/organizations', label: 'Open Organizations' },
    sections: [
      { type: 'paragraph', text: 'An Organization is a shared workspace. Posts created under an org are visible to every member, not just the creator. Use orgs when multiple people need to collaborate on the same content pipeline.' },
      { type: 'heading', text: 'Steps' },
      { type: 'steps', items: [
        'Open /organizations.',
        'Create an org — name + optional description.',
        'Invite team members by email. They\'ll get an invite link.',
        'When generating a post, optionally pick the org from the dropdown. That post belongs to the org.',
        'Switch between personal and org workspaces from the workspace picker.',
      ]},
    ],
  },
  {
    id: 'export',
    title: 'CSV Export',
    category: 'workflow',
    icon: Download,
    summary: 'Download your content as a CSV for handoff to clients or reporting.',
    tryLink: { href: '/export', label: 'Open Export' },
    sections: [
      { type: 'paragraph', text: 'Export any set of posts to CSV — useful for client approval, reporting, or bulk editing in a spreadsheet.' },
      { type: 'heading', text: 'Steps' },
      { type: 'steps', items: [
        'Open /export.',
        'Filter by date range, platform, status, or client.',
        'Click "Export CSV" — the file downloads immediately.',
      ]},
      { type: 'tip', text: 'Exported CSVs contain platform, scheduled_for, caption, hashtags, status, published_url, and client_name — ready for a spreadsheet client report.' },
    ],
  },

  // ─── Account & Billing ──────────────────────────────────────────────
  {
    id: 'subscription',
    title: 'Subscription & billing',
    category: 'account',
    icon: CreditCard,
    summary: 'View your plan, usage, and upgrade when you hit limits.',
    tryLink: { href: '/subscription', label: 'Open Subscription' },
    sections: [
      { type: 'paragraph', text: 'The Subscription page shows your current plan, this month\'s usage, and all available tiers.' },
      { type: 'heading', text: 'Plans at a glance' },
      { type: 'list', items: [
        'Free — 5 posts + 5 AI images / month, free forever. Upgrade when you need more.',
        'Starter — $99/mo, 300 posts, 300 AI images',
        'Growth (Most Popular) — $159/mo, 800 posts, 800 AI images, Multi-Account, AI Auto-Scheduler',
        'Pro — $199/mo, 1,500 posts, 1,500 AI images, AI Post Manager fully automated, Premium support',
      ]},
      { type: 'heading', text: 'How to upgrade' },
      { type: 'steps', items: [
        'Click the usage meter in the sidebar OR hit a quota limit — the Upgrade Modal opens.',
        'Pick a tier. Click "Upgrade to [Tier]".',
        'You\'re handed off to Stripe Checkout. Complete payment.',
        'Access to the new tier is instant on return.',
      ]},
      { type: 'tip', text: 'Cancel anytime from /subscription. You keep access until the end of the current billing period.' },
    ],
  },
  {
    id: 'security',
    title: 'Security & 2FA',
    category: 'account',
    icon: Shield,
    summary: 'Email verification, two-factor auth (TOTP or email OTP), and password reset.',
    tryLink: { href: '/settings', label: 'Open Settings' },
    sections: [
      { type: 'paragraph', text: 'Every account requires email verification before use. You can optionally enable 2FA for a second factor on every login.' },
      { type: 'heading', text: 'Enabling 2FA' },
      { type: 'steps', items: [
        'Open /settings → Security section.',
        'Choose a method: Authenticator App (TOTP) or Email OTP.',
        'For TOTP: scan the QR code with Google Authenticator / Authy / 1Password, then enter the 6-digit code to confirm.',
        'Save backup codes somewhere safe — they let you log in if you lose your authenticator.',
      ]},
      { type: 'heading', text: 'Password reset' },
      { type: 'paragraph', text: 'If you\'re locked out, click "Forgot password" on the login page. A reset link gets emailed to your verified address; it expires after 1 hour.' },
      { type: 'warning', text: 'Never share your backup codes or TOTP seed. If you lose both your device and backup codes, account recovery requires support.' },
    ],
  },
  {
    id: 'usage-meter',
    title: 'Usage meter',
    category: 'account',
    icon: Users,
    summary: 'Track your monthly AI images and posts in real time.',
    sections: [
      { type: 'paragraph', text: 'The usage meter lives in the sidebar just above your profile. It shows your current plan and how much of your monthly quota you\'ve used.' },
      { type: 'heading', text: 'What the colors mean' },
      { type: 'list', items: [
        'Gray bars — plenty of quota left',
        'Amber bars — you\'re at 80%+ used, consider upgrading soon',
        'Red bars with "Upgrade →" — you\'ve hit the cap; new generations are blocked',
      ]},
      { type: 'tip', text: 'Click the meter at any time to see the Upgrade Modal. Upgrading is prorated — you only pay the difference for the rest of the month.' },
    ],
  },

  // ─── Platform setup (App Review) ────────────────────────────────────
  {
    id: 'meta-app-review',
    title: 'Meta App Review (Facebook + Instagram)',
    category: 'platform-setup',
    icon: ShieldCheck,
    summary: 'Step-by-step submission for `pages_manage_posts` + Instagram publishing — including exact answers for the Data Handling form.',
    tryLink: { href: 'https://developers.facebook.com/apps/', label: 'Open Meta Developer Portal' },
    sections: [
      { type: 'paragraph', text: 'Meta requires App Review before your app can post on behalf of any Page or Instagram Business account that you don\'t personally admin. Reviews typically take 3-7 business days. This tutorial gives you the exact answers and assets so you pass on the first try.' },
      { type: 'warning', text: 'Before you start, make sure: (1) your app is set to "Live" mode, (2) you have a Privacy Policy and Terms URL on posttosocial.com, (3) you have a screen recording showing the full flow.' },

      { type: 'heading', text: 'Permissions to request' },
      { type: 'list', items: [
        'pages_manage_posts — required to publish to a Facebook Page',
        'pages_read_engagement — required to read Page metadata when listing Pages',
        'pages_show_list — required to list which Pages the user admins',
        'instagram_basic — required to read the IG Business account',
        'instagram_content_publish — required to publish images/videos to IG',
        'business_management — only if you want to access Pages owned by a Business Manager',
      ]},

      { type: 'heading', text: 'Step 1 — App Settings & Basic info' },
      { type: 'steps', items: [
        'Privacy Policy URL: https://posttosocial.com/privacy',
        'Terms of Service URL: https://posttosocial.com/terms',
        'App Domains: posttosocial.com',
        'Category: "Business and Pages"',
        'Save changes (this unlocks the App Review tab).',
      ]},

      { type: 'heading', text: 'Step 2 — Use case + screencast' },
      { type: 'steps', items: [
        'Open Use Cases → "Customize" on "Authentication and account creation" + "Custom" → Add the permissions list above.',
        'For each permission, paste a one-paragraph use case (sample below).',
        'Record a 60-90 second screen capture showing: 1) Login → 2) Click "Connect Facebook" → 3) Approve scopes → 4) Page picker shows real Pages → 5) Schedule a post → 6) Post lives on the Page',
        'Upload the recording as the App Review video.',
      ]},
      { type: 'tip', text: 'Sample use-case copy you can paste verbatim: "PostToSocial is a SaaS marketing tool that lets small business owners and agencies draft, schedule, and publish posts to their own Facebook Pages and Instagram Business accounts. After OAuth, we use pages_manage_posts to publish exactly the content the user reviewed and scheduled in our app — no autonomous posting. Users can disconnect at any time from /connections."' },

      { type: 'heading', text: 'Step 3 — Data Handling form (the questions you\'re looking at now)' },
      { type: 'paragraph', text: 'Answer these EXACTLY as below — they match how PostToSocial is actually built:' },
      { type: 'list', items: [
        '"Do you have data processors or service providers ...?" → YES. List: MongoDB Atlas (database), Cloudflare (CDN/network protection), the cloud hosting provider you use (e.g., Google Cloud / Render / Vercel — whatever powers posttosocial.com), Stripe (payments), SendGrid (transactional email).',
        '"Do you transfer Platform Data outside the EEA/UK?" → YES if your servers are US-based. Mention Standard Contractual Clauses (SCCs) are in place via your cloud + MongoDB Atlas DPAs.',
        '"How long do you retain Platform Data?" → "For the duration of the user\'s active subscription. On disconnect or account deletion, all OAuth tokens, page IDs, and Meta-derived metadata are purged within 30 days."',
        '"Do you sell Platform Data?" → NO.',
        '"Do you use Platform Data to build/train ML models?" → NO.',
        '"Where is Platform Data stored?" → "Encrypted at rest in MongoDB Atlas (US region), accessed only by the PostToSocial backend over TLS 1.3."',
      ]},

      { type: 'heading', text: 'Step 4 — Data Deletion callback' },
      { type: 'paragraph', text: 'Meta requires a webhook URL where they can ping you when a user requests data deletion. PostToSocial exposes this at:' },
      { type: 'list', items: [
        'Data Deletion Request URL: https://posttosocial.com/api/meta/data-deletion',
        'User Data Deletion Instructions URL: https://posttosocial.com/legal/data-deletion',
      ]},
      { type: 'warning', text: 'If those URLs return 404, ask the platform owner to enable the Meta deletion endpoints (server.py — `meta_data_deletion_router`). They\'re a hard requirement for App Review.' },

      { type: 'heading', text: 'Step 5 — Test users' },
      { type: 'steps', items: [
        'In the Meta Developer Portal → Roles → Test Users, create one test user.',
        'Have that test user accept admin on a real (or test) Facebook Page + linked Instagram Business account.',
        'Add their credentials to your App Review submission ("Credentials for review").',
        'This lets the Meta reviewer log in and reproduce the publishing flow without affecting your real users.',
      ]},

      { type: 'heading', text: 'Step 6 — Submit + monitor' },
      { type: 'list', items: [
        'Click "Submit for Review" — typical wait time is 3-7 business days.',
        'Check the developer email daily; rejections include a video time stamp showing what went wrong.',
        'Most common rejection: reviewer can\'t see the connected Page actually post — make sure your screencast clearly shows the post landing on Facebook.',
      ]},

      { type: 'tip', text: 'Once approved, your Meta app moves to "Live" and ALL users (not just admins) can connect their own Pages and IG accounts. No code changes needed in PostToSocial.' },
    ],
  },
  {
    id: 'linkedin-app-review',
    title: 'LinkedIn Marketing Developer Platform',
    category: 'platform-setup',
    icon: ShieldCheck,
    summary: 'Get LinkedIn approval to post to Company Pages — required for `w_organization_social` scope.',
    tryLink: { href: 'https://www.linkedin.com/developers/apps', label: 'Open LinkedIn Developer Portal' },
    sections: [
      { type: 'paragraph', text: 'By default LinkedIn only lets your app post to a user\'s personal profile (`w_member_social`). To post to LinkedIn Company Pages you need the Marketing Developer Platform / Community Management API — a manual approval LinkedIn typically completes in 5-10 business days.' },

      { type: 'heading', text: 'What you\'re applying for' },
      { type: 'list', items: [
        'w_organization_social — publish posts to a Page your member admins',
        'r_organization_social — read posts/comments on those Pages',
        'rw_organization_admin — list the Pages a member admins',
      ]},

      { type: 'heading', text: 'Step 1 — Apply for Marketing Developer Platform' },
      { type: 'steps', items: [
        'Open your app at linkedin.com/developers/apps → click your PostToSocial app.',
        'Open the "Products" tab.',
        'Click "Request access" on "Community Management API" (formerly Marketing Developer Platform).',
        'Fill in: company name, company URL (posttosocial.com), use-case description, monthly post volume estimate.',
        'Attach a 60-90 second demo video showing: connect → page picker → schedule a post → post lives on the LinkedIn Company Page.',
      ]},

      { type: 'tip', text: 'Sample use-case description: "PostToSocial is a multi-tenant SaaS marketing tool. After OAuth, agency users connect the Company Pages they personally admin and use our generator to draft posts. We only call /ugcPosts on the user\'s explicit schedule — no autonomous posting. Posts are pre-reviewed by the user via our drafts UI before publishing."' },

      { type: 'heading', text: 'Step 2 — Switch scopes once approved' },
      { type: 'steps', items: [
        'When the email approval lands, log in to PostToSocial as the platform owner.',
        'Go to Admin → Platforms → LinkedIn → click Edit.',
        'In `extra_config.scope`, paste: openid profile email w_member_social w_organization_social r_organization_social rw_organization_admin',
        'Save and disconnect/reconnect LinkedIn to pick up the new scopes.',
      ]},

      { type: 'warning', text: 'LinkedIn rejects apps that don\'t show a clear "user picks which Page to post to" screen. Make sure your demo video shows the Page picker (not just a one-button auto-connect).' },
    ],
  },
];

export const getTutorialById = (id) => TUTORIALS.find((t) => t.id === id);
export const getTutorialsByCategory = (catId) => TUTORIALS.filter((t) => t.category === catId);
